import cx from "classnames"

import type {
  SkeletonFontSizeType,
  SkeletonLineHeightType,
  SkeletonBgColorsType,
  SkeletonBorderType,
  SkeletonShapeType,
  SkeletonAnimationType,
  SkeletonSizeType,
} from "./types"

import styles from "./index.module.css"
import {
  SKELETON_FONT_SIZES,
  SKELETON_LINE_HEIGHT,
  SKELETON_BG_COLORS,
  SKELETON_BORDER,
  SKELETON_SHAPES,
  SKELETON_ANIMATION,
  SKELETON_SIZES,
} from "./types"

/**
 * Props for the Skeleton component
 * @interface SkeletonProps
 */
export interface SkeletonProps {
  /**
   * Width of the skeleton
   */
  width?: React.CSSProperties["width"]
  /**
   * Height of the skeleton
   */
  height?: React.CSSProperties["height"]
  /**
   * Background color of the skeleton
   * @default "background"
   */
  bgColor?: SkeletonBgColorsType
  /**
   * Border style of the skeleton
   */
  border?: SkeletonBorderType
  /**
   * Shape of the skeleton
   * @default "rectangle"
   */
  shape?: SkeletonShapeType
  /**
   * Animation type
   * @default "wave"
   */
  animation?: SkeletonAnimationType
  /**
   * Predefined size preset
   */
  size?: SkeletonSizeType
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Accessible label for screen readers
   */
  "aria-label"?: string
  /**
   * Whether the skeleton represents loading content
   * @default true
   */
  "aria-busy"?: boolean
}

/**
 * Props for the SkeletonText component
 * @interface SkeletonTextProps
 */
export interface SkeletonTextProps {
  /**
   * Font size of the skeleton text
   * @default "m"
   */
  fontSize?: SkeletonFontSizeType
  /**
   * Line height of the skeleton text
   * @default "medium"
   */
  lineHeight?: SkeletonLineHeightType
  /**
   * Number of lines to render
   * @default 1
   */
  lines?: number
  /**
   * Width of each line
   * @default "100%"
   */
  width?: React.CSSProperties["width"]
  /**
   * Width of the last line (only applied when in the last line). It should be a percentage value
   * @default "70%"
   */
  lastLineWidth?: React.CSSProperties["width"]
  /**
   * Background color of the skeleton
   * @default "background"
   */
  bgColor?: SkeletonBgColorsType
  /**
   * Border style of the skeleton
   */
  border?: SkeletonBorderType
  /**
   * Animation type
   * @default "wave"
   */
  animation?: SkeletonAnimationType
  /**
   * Gap between lines
   * @default "0.25rem"
   */
  gap?: React.CSSProperties["gap"]
  /**
   * Accessible label for screen readers
   */
  "aria-label"?: string
  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Skeleton component for displaying loading placeholders
 * @param {SkeletonProps} props - Component props
 * @returns {JSX.Element} Skeleton element
 */
export const Skeleton = ({
  ref,
  width,
  height,
  bgColor = "background",
  border,
  shape = "rectangle",
  animation = "wave",
  size,
  className,
  "aria-label": ariaLabel = "Loading content",
  "aria-busy": ariaBusy = true,
  ...rest
}: SkeletonProps & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  // Apply predefined size if provided
  const sizePreset = size ? SKELETON_SIZES[size] : null
  const finalWidth = width ?? sizePreset?.width
  const finalHeight = height ?? sizePreset?.height
  const finalShape = sizePreset?.shape ?? shape

  return (
    <div
      ref={ref}
      role="status"
      aria-label={ariaLabel}
      aria-busy={ariaBusy}
      className={cx(
        styles.skeleton,
        styles[SKELETON_BG_COLORS[bgColor]],
        styles[SKELETON_SHAPES[finalShape]],
        styles[SKELETON_ANIMATION[animation]],
        border && styles[SKELETON_BORDER[border]],
        className,
      )}
      style={{
        width: finalWidth,
        height: finalHeight,
      }}
      {...rest}
    />
  )
}

Skeleton.displayName = "Skeleton"

/**
 * SkeletonText component for displaying text loading placeholders
 * Renders multiple Skeleton components as lines of text
 * @param {SkeletonTextProps} props - Component props
 * @returns {JSX.Element} Container with Skeleton elements representing text lines
 */
export const SkeletonText = ({
  ref,
  fontSize = "m",
  lineHeight = "medium",
  lines = 1,
  width = "100%",
  lastLineWidth = "70%",
  bgColor = "background",
  border,
  animation = "wave",
  gap = "0.25rem",
  "aria-label": ariaLabel = `Loading ${lines} line${lines > 1 ? "s" : ""} of text`,
  className,
  ...rest
}: SkeletonTextProps & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  const lineHeightValue = `var(${SKELETON_LINE_HEIGHT[lineHeight]})`
  const fontSizeValue = `var(${SKELETON_FONT_SIZES[fontSize]})`
  const totalHeight = `calc(${lineHeightValue} * ${fontSizeValue})`

  return (
    <div
      ref={ref}
      role="status"
      aria-label={ariaLabel}
      aria-busy={true}
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        gap,
      }}
      {...rest}
    >
      {Array.from({ length: lines }, (_, index) => {
        const isLastLine = index === lines - 1 && lines > 1
        const lineWidth = isLastLine ? lastLineWidth : width

        return (
          <Skeleton
            key={index}
            height={totalHeight}
            width={lineWidth}
            bgColor={bgColor}
            border={border}
            animation={animation}
            aria-label=""
            aria-busy={false}
          />
        )
      })}
    </div>
  )
}

SkeletonText.displayName = "SkeletonText"

// Convenience components for common use cases
export const SkeletonAvatar = ({
  ref,
  ...props
}: Omit<SkeletonProps, "size"> & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <Skeleton ref={ref} size="avatar" {...props} />
)

SkeletonAvatar.displayName = "SkeletonAvatar"

export const SkeletonButton = ({
  ref,
  ...props
}: Omit<SkeletonProps, "size"> & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <Skeleton ref={ref} size="button" {...props} />
)

SkeletonButton.displayName = "SkeletonButton"

export const SkeletonTitle = ({
  ref,
  ...props
}: Omit<SkeletonTextProps, "size"> & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <SkeletonText ref={ref} fontSize="xl" width="60%" lines={1} {...props} />
)

SkeletonTitle.displayName = "SkeletonTitle"
